import { useAppStore } from "../store/appStore";
import { Ico } from "./icons";
import { Avatar } from "./atoms";

function Brand() {
  return (
    <div className="sidebar-brand">
      <span className="brand-mark">cf</span>
      <span className="brand-wordmark">Claude<em>Flow</em></span>
    </div>
  );
}

function SidebarFoot() {
  const me = useAppStore((s) => s.me);
  if (!me) return null;
  return (
    <div className="sidebar-foot">
      <Avatar member={me} size={26} isSelf />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="name">{me.displayName}</div>
        <div className="meta">{me.linuxUser}@claude.max</div>
      </div>
      <button className="icon-btn" title="Settings"><Ico name="settings" size={14} /></button>
    </div>
  );
}

interface SidebarProps {
  onSpawnAgent: () => void;
}

export function Sidebar({ onSpawnAgent }: SidebarProps) {
  const variant = useAppStore((s) => s.sidebarVariant);
  return variant === "team"
    ? <SidebarTeamFocus onSpawnAgent={onSpawnAgent} />
    : <SidebarWorkspaces onSpawnAgent={onSpawnAgent} />;
}

function SidebarWorkspaces({ onSpawnAgent }: SidebarProps) {
  const {
    workspaces, activeWorkspace, setActiveWorkspace, team, me,
    openWorkspaceModal, openTeammate,
  } = useAppStore();
  const onlineCount = team.filter((t) => t.online).length;

  return (
    <aside className="sidebar">
      <Brand />
      <div className="sidebar-scroll">
        <div className="sidebar-section">
          <div className="sidebar-section-head">
            <span className="eyebrow">Workspaces</span>
            <button className="icon-btn" onClick={openWorkspaceModal} title="Workspaces verwalten">
              <Ico name="folder" size={13} />
            </button>
          </div>
          {workspaces.map((w) => (
            <div
              key={w.id}
              className={`sidebar-row ${activeWorkspace === w.id ? "active" : ""}`}
              onClick={() => setActiveWorkspace(w.id)}
            >
              <span className="dot" style={{ background: w.dot }} />
              <span className="sidebar-row-label">{w.name}</span>
              {w.agents > 0 && <span className="sidebar-row-badge">{w.agents}</span>}
            </div>
          ))}
          {workspaces.length === 0 && (
            <div style={{ padding: "0.4rem 1.25rem", fontSize: 12, color: "var(--color-fg-subtle)" }}>
              Noch keine Workspaces.
            </div>
          )}
        </div>

        <div className="sidebar-section">
          <div className="sidebar-section-head">
            <span className="eyebrow">Team · {onlineCount} online</span>
            <button className="icon-btn" title="Aktivitäten"><Ico name="users" size={13} /></button>
          </div>
          {team.map((t) => {
            const isSelf = me?.supabaseId === t.supabaseId;
            return (
              <div key={t.supabaseId} className="team-row" onClick={() => !isSelf && openTeammate(t.supabaseId)}>
                <Avatar member={t} isSelf={isSelf} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="name">
                    {t.displayName}
                    {isSelf && <span className="subtle" style={{ marginLeft: 4, fontSize: 11 }}> (du)</span>}
                  </div>
                  <div className="meta">
                    <b>{t.agents}</b> Session{t.agents !== 1 ? "s" : ""}
                    {t.branch ? <> · <span style={{ fontFamily: "var(--font-mono)" }}>{t.branch}</span></> : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <button className="sidebar-cta" onClick={onSpawnAgent}>
          <Ico name="plus" size={14} />
          Neue Session
          <span className="kbd">⌘⇧N</span>
        </button>
      </div>
      <SidebarFoot />
    </aside>
  );
}

function SidebarTeamFocus({ onSpawnAgent }: SidebarProps) {
  const {
    workspaces, activeWorkspace, setActiveWorkspace, team, me,
    openWorkspaceModal, openTeammate, panels,
  } = useAppStore();

  return (
    <aside className="sidebar">
      <Brand />
      <div className="sidebar-scroll">
        <div className="sidebar-section">
          <div className="sidebar-section-head">
            <span className="eyebrow">Workspaces</span>
            <button className="icon-btn" onClick={openWorkspaceModal}><Ico name="folder" size={13} /></button>
          </div>
          {workspaces.slice(0, 3).map((w) => (
            <div
              key={w.id}
              className={`sidebar-row ${activeWorkspace === w.id ? "active" : ""}`}
              onClick={() => setActiveWorkspace(w.id)}
            >
              <span className="dot" style={{ background: w.dot }} />
              <span className="sidebar-row-label">{w.name}</span>
              {w.agents > 0 && <span className="sidebar-row-badge">{w.agents}</span>}
            </div>
          ))}
        </div>

        <div className="sidebar-section">
          <div className="sidebar-section-head">
            <span className="eyebrow">Team</span>
          </div>
          {team.map((t) => {
            const isSelf = me?.supabaseId === t.supabaseId;
            const sessions = panels.filter((p) => p.ownerSupabaseId === t.supabaseId);
            return (
              <div key={t.supabaseId} className="team-block" onClick={() => !isSelf && openTeammate(t.supabaseId)}>
                <div className="head">
                  <Avatar member={t} isSelf={isSelf} />
                  <span className="name">{t.displayName}{isSelf && <span className="subtle" style={{ fontSize: 10, marginLeft: 4 }}>du</span>}</span>
                  <span className="meta">{t.agents} live</span>
                </div>
                {!isSelf && sessions.slice(0, 3).map((s) => (
                  <div key={s.agentId} className="mini-session">
                    <span
                      className="dot"
                      style={{ background: s.status === "running" ? "var(--color-running)" : "var(--color-thinking)" }}
                    />
                    <span className="task">{s.task}</span>
                  </div>
                ))}
                {isSelf && (
                  <div className="mini-session" style={{ color: "var(--color-fg-subtle)", fontStyle: "italic" }}>
                    {sessions.length} eigene Sessions · siehe Panels
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <SidebarFoot />
    </aside>
  );
}
